import { Fragment, useState } from 'react'
import { Button, Chip, Input } from '@heroui/react'
import { api } from '../../lib/api'
import {
  LEVEL_ORDERS,
  MAX_LEVELS,
  levelProblem,
  takenElsewhere,
  type BasicCallingProfile,
  type BasicLevel,
  type BasicProfileIn,
  type BasicQueue,
  type LevelOrder,
} from '../../lib/queueConfig'
import { nameIn, type StaffKey, type StaffLanguage } from '../../lib/staffText'
import { Icon } from '../../ui/icons'
import { MultiSelect, type PickOption } from '../../ui/MultiSelect'
import { EmptyState } from '../../ui/parts'
import { useStaffText } from '../../ui/StaffText'
import { Loading } from '../Loading'
import { Confirm, useFlash } from '../sectionKit'
import { IconTile, InnerPage, ListCard, ListGroupRow, ListRow, ListTable, NameCell, Notice, PageHead, RowArrow, toneOf, useOpenRecord } from '../settingsKit'
import { Pick } from '../setupKit'
import { NotBasicNote, ReadOnlyNote, refusalText, useQueueScreen } from './shared'
import '../../styles/branch.css'

/** Queue management › Calling profiles: named calling orders, level 1 first. */
export function CallingProfiles({
  organisation,
  readOnly,
  onAdvanced,
}: {
  organisation?: string
  readOnly: boolean
  onAdvanced: () => void
}) {
  const { lang, say } = useStaffText()
  const { screen, error, write } = useQueueScreen(organisation)
  const { flash, toast } = useFlash()
  const editable = Boolean(screen?.editable) && !readOnly
  const setups = screen?.setups ?? []
  // A calling profile opens on its own page (#callingProfiles/<id>, #callingProfiles/new); read-only opens none.
  const rec = useOpenRecord(
    setups.flatMap((s) => s.callingProfiles),
    (p) => p.id,
    Boolean(screen),
  )
  const editing: BasicCallingProfile | false | undefined =
    editable && (rec.isNew ? { id: '', name: '', levels: [{ queueIds: [] }] } : rec.row)
  const total = setups.reduce((n, s) => n + s.callingProfiles.length, 0)
  const at = { baseRevision: screen?.revision, ...(organisation ? { organisation } : {}) }

  if (editing) {
    return (
      <>
        {toast}
        <CallingProfileSheet
          key={rec.code}
          profile={editing}
          queues={screen?.setups[0]?.queues ?? []}
          onClose={rec.close}
          onSave={async (draft) => {
            await write(api.saveCallingProfile(editing.id, draft, at))
            rec.close()
            flash(say('catalogue.calling.saved'))
          }}
          onDelete={
            editing.id
              ? async () => {
                  await write(api.deleteCallingProfile(editing.id, at))
                  rec.close()
                  flash(say('catalogue.calling.deleted'))
                }
              : undefined
          }
        />
      </>
    )
  }

  return (
    <>
      <PageHead
        title={say('admin.section.callingProfiles')}
        line={say('settings.calling.line')}
        actions={
          editable ? (
            <Button variant="primary" onPress={() => rec.open()}>
              <Icon name="plus" size={16} /> {say('catalogue.calling.add')}
            </Button>
          ) : undefined
        }
      />
      {toast}
      {screen && screen.mode !== 'basic' && <ReadOnlyNote onAdvanced={onAdvanced} />}
      {screen?.mode === 'basic' && screen.problems.length > 0 && <NotBasicNote problems={screen.problems} />}
      {error && <Notice>{error}</Notice>}

      <ListCard title={say('settings.calling.list')} count={screen ? say('settings.calling.count', { n: total }) : undefined}>
        {!screen && <Loading />}
        {screen && total === 0 && !error && <EmptyState icon={<Icon name="people" size={20} />} title={say('catalogue.calling.none')} />}
        {total > 0 && (
          <ListTable
            label={say('settings.calling.list')}
            cols={`minmax(200px, 1fr) minmax(260px, 1.6fr) ${editable ? '16px' : '0px'}`}
            head={[say('settings.calling.col.profile'), say('settings.calling.col.calls'), '']}
          >
            {setups.map((setup) =>
              setup.callingProfiles.length === 0 ? null : (
                <Fragment key={setup.id}>
                  {setups.length > 1 && <ListGroupRow name={setup.name} />}
                  {setup.callingProfiles.map((p) => (
                    <ListRow key={p.id} onOpen={editable ? () => rec.open(p) : undefined}>
                      <NameCell
                        tile={<IconTile icon="callIn" tone={toneOf(p.id)} />}
                        name={p.name}
                        sub={say('settings.calling.levels', { n: p.levels.length })}
                      />
                      <ol role="cell" className="cp-levels">
                        {p.levels.map((level, i) => (
                          <li key={i}>
                            <span className="cp-levels__n" aria-label={say('catalogue.calling.level', { n: i + 1 })}>
                              {i + 1}
                            </span>
                            <span className="cp-levels__q">{level.queueIds.map((id) => queueName(setup.queues, id, lang)).join(' + ')}</span>
                            {level.order === 'closest_to_target' && (
                              <Chip size="sm" variant="secondary">
                                {say(orderKey(level.order))}
                              </Chip>
                            )}
                          </li>
                        ))}
                      </ol>
                      {editable ? <RowArrow /> : <span />}
                    </ListRow>
                  ))}
                </Fragment>
              ),
            )}
          </ListTable>
        )}
      </ListCard>
    </>
  )
}

const orderKey = (order: LevelOrder | undefined) => `catalogue.calling.order.${order ?? 'max_waiting'}` as StaffKey

const queueName = (queues: readonly BasicQueue[], id: string, lang: StaffLanguage) => {
  const q = queues.find((x) => x.id === id)
  return q ? nameIn(q.name, lang, q.code) : '?'
}

function CallingProfileSheet({
  profile,
  queues,
  onClose,
  onSave,
  onDelete,
}: {
  profile: BasicCallingProfile
  queues: BasicQueue[]
  onClose: () => void
  onSave: (draft: BasicProfileIn) => Promise<void>
  onDelete?: () => Promise<void>
}) {
  const { lang, say } = useStaffText()
  const [name, setName] = useState(profile.name)
  const [levels, setLevels] = useState<BasicLevel[]>(() =>
    profile.levels.length ? profile.levels.map((l) => ({ ...l, queueIds: [...l.queueIds] })) : [{ queueIds: [] }],
  )
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const problem = levelProblem(name, levels)

  const setLevel = (i: number, next: Partial<BasicLevel>) => setLevels((all) => all.map((l, k) => (k === i ? { ...l, ...next } : l)))
  const remove = (i: number) => {
    setLevels((all) => (all.length > 1 ? all.filter((_, k) => k !== i) : all))
  }
  const act = async (fn: () => Promise<void>) => {
    setBusy(true)
    setError('')
    try {
      await fn()
    } catch (err) {
      setError(refusalText(err, lang, say, queues))
    } finally {
      setBusy(false)
    }
  }

  return (
    <InnerPage
      back={say('admin.section.callingProfiles')}
      asForm={false}
      title={profile.id ? say('catalogue.calling.editTitle') : say('catalogue.calling.new')}
      onClose={onClose}
      onSubmit={() => void act(() => onSave({ name: name.trim(), levels }))}
      submitLabel={say('common.save')}
      valid={problem === ''}
      busy={busy}
      error={error}
      extra={
        onDelete ? (
          <Confirm
            ask={say('common.delete')}
            question={say('catalogue.calling.deleteAsk')}
            yes={say('common.delete')}
            keep={say('ui.keepEditing')}
            busy={busy}
            onYes={() => void act(onDelete)}
          />
        ) : problem === 'empty' ? (
          say('catalogue.calling.needQueue')
        ) : undefined
      }
    >
      <label className="field">
        <span>{say('catalogue.calling.name')}</span>
        <Input
          fullWidth
          value={name}
          maxLength={64}
          placeholder={say('catalogue.calling.namePlaceholder')}
          onChange={(e) => setName(e.target.value)}
        />
      </label>
      <div className="admin-levels">
        {levels.map((level, i) => (
          <LevelPicker
            key={i}
            index={i}
            ids={level.queueIds}
            order={level.order ?? 'max_waiting'}
            queues={queues}
            taken={takenElsewhere(levels, i)}
            takenAt={(id) => levels.findIndex((l, k) => k !== i && l.queueIds.includes(id))}
            onChange={(queueIds) => setLevel(i, { queueIds })}
            onOrder={(order) => setLevel(i, { order })}
            onRemove={levels.length > 1 ? () => remove(i) : undefined}
          />
        ))}
        <Button
          variant="outline"
          fullWidth
          isDisabled={levels.length >= MAX_LEVELS}
          onPress={() => {
            setLevels((all) => (all.length < MAX_LEVELS ? [...all, { queueIds: [] }] : all))
          }}
        >
          <Icon name="plus" size={16} /> {say('catalogue.calling.addLevel')}
          <span className="cp-addlevel__n">
            {levels.length} / {MAX_LEVELS}
          </span>
        </Button>
      </div>
    </InnerPage>
  )
}

/** One level: ui/MultiSelect over the profile's queues; a queue in another level is disabled. */
function LevelPicker({
  index,
  ids,
  order,
  queues,
  taken,
  takenAt,
  onChange,
  onOrder,
  onRemove,
}: {
  index: number
  ids: string[]
  order: LevelOrder
  queues: BasicQueue[]
  taken: Set<string>
  /** The other level (0-based) holding this queue, or -1. */
  takenAt: (id: string) => number
  onChange: (ids: string[]) => void
  onOrder: (order: LevelOrder) => void
  onRemove?: () => void
}) {
  const { lang, say } = useStaffText()
  const options: PickOption[] = queues.map((q) => ({
    value: q.id,
    label: nameIn(q.name, lang, q.code),
    note: taken.has(q.id) ? say('catalogue.calling.inLevel', { n: takenAt(q.id) + 1 }) : q.letter,
    disabled: taken.has(q.id),
  }))
  return (
    <div className="admin-level">
      <div className="admin-level__head">
        <strong>{say('catalogue.calling.level', { n: index + 1 })}</strong>
        <Pick
          size="sm"
          className="admin-level__order"
          label={say('catalogue.calling.orderFor', { n: index + 1 })}
          value={order}
          onChange={(v) => onOrder(v as LevelOrder)}
          options={LEVEL_ORDERS.map((o) => ({ value: o, label: say(orderKey(o)) }))}
        />
        {onRemove && (
          <Button variant="ghost" size="sm" onPress={onRemove}>
            {say('common.remove')}
          </Button>
        )}
      </div>
      <MultiSelect
        options={options}
        value={ids}
        onChange={onChange}
        placeholder={say('catalogue.calling.select')}
        label={say('catalogue.calling.selectFor', { n: index + 1 })}
        searchPlaceholder={say('catalogue.calling.search')}
      />
    </div>
  )
}

